import { Box, Typography, Collapse, CircularProgress } from '@mui/material';
import { Link } from 'react-router-dom';
import { ChevronRight, ChevronDown, ExternalLinkIcon } from '@assets/svgs';
import { PolicyItem } from '../types';
import { TermGroup } from './TermGroup';
import { LoadMoreButton } from './LoadMoreButton';
import { usePolicyTransactionsGrouped } from '../hooks/usePolicyTransactionsGrouped';
import { getPolicyStatusLabel } from '../utils/formatters';
import { hasMultipleTerms } from '../utils/groupTransactionsByTerm';
import { getFormattedPolicyNumber } from '@utils/policy/getFormattedPolicyNumber';
import { externalLinkIconStyle } from '../utils/constants';

interface PolicySectionProps {
  policy: PolicyItem;
  isExpanded: boolean;
  onToggle: () => void;
  currentTransactionId?: string;
  currentPolicyId?: string;
}

export const PolicySection = ({
  policy,
  isExpanded,
  onToggle,
  currentTransactionId,
  currentPolicyId,
}: PolicySectionProps) => {
  const isCurrentPolicy = policy.id === currentPolicyId;

  const { termGroups, isLoading, hasMore, loadMore } = usePolicyTransactionsGrouped({
    policyId: policy.id,
    enabled: isExpanded,
  });

  // Get term count for display
  const termCount = policy.policyTerm?.termCount;

  // Format policy number with prefix and term suffix
  const policyNumber = getFormattedPolicyNumber(
    policy.policyNumberPrefix || '',
    policy.policyNumber?.policyNumber || '',
    termCount,
  );

  const offering = policy.boundQuote?.offering?.insuranceProduct?.name || '';
  const status = getPolicyStatusLabel(policy.status || '');
  const packageLabel = policy.packageLabel || '';

  const showTermHeaders = hasMultipleTerms(termGroups);

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        role='button'
        tabIndex={0}
        aria-expanded={isExpanded}
        aria-label={`${isExpanded ? 'Collapse' : 'Expand'} policy ${policyNumber}`}
        onClick={onToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          cursor: 'pointer',
          py: 1,
          px: 2,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              width: 'fit-content',
            }}
          >
            <Link
              to={`/policy/${policy.id}`}
              target='_blank'
              rel='noopener noreferrer'
              onClick={(e) => e.stopPropagation()}
              style={{ textDecoration: 'none' }}
            >
              <Typography
                variant='body2'
                sx={{
                  color: 'secondary.main',
                  fontWeight: 700,
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                {policyNumber}
                {isCurrentPolicy ? '*' : ''}{' '}
                <ExternalLinkIcon width='10px' height='10px' style={externalLinkIconStyle} />
              </Typography>
            </Link>
          </Box>
          <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
            {offering} | {status}
          </Typography>
          {packageLabel && (
            <Typography variant='caption' color='text.secondary' sx={{ display: 'block' }}>
              {packageLabel}
            </Typography>
          )}
        </Box>
        <Box sx={{ ml: 1, mt: 0.5 }}>
          {isExpanded ? <ChevronDown width='12px' height='12px' /> : <ChevronRight width='12px' height='12px' />}
        </Box>
      </Box>

      <Collapse in={isExpanded}>
        <Box sx={{ pl: 1 }}>
          {isLoading && termGroups.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <>
              {termGroups.map((termGroup) => (
                <TermGroup
                  key={termGroup.termNumber}
                  termGroup={termGroup}
                  currentTransactionId={currentTransactionId}
                  showTermHeader={showTermHeaders}
                />
              ))}
              <LoadMoreButton onClick={loadMore} isLoading={isLoading} hasMore={hasMore} />
            </>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
